// utils/exportImage.ts
import { toPng } from 'html-to-image';
import { colors } from '@/styles/base/tokens';

export function downloadBlob(filename: string, blob: Blob): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export async function copyBlobToClipboard(blob: Blob): Promise<void> {
    if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
    } else {
        throw new Error('Clipboard image copy not supported in this browser.');
    }
}

/** Exclude elements marked with 'export-exclude' class from captures. */
const exportFilter = (node: HTMLElement): boolean => {
    return !node.classList?.contains('export-exclude');
};

/**
 * Temporarily expand all scroll-constrained containers so the full content
 * is visible for capture. Returns a restore function.
 */
function expandScrollContainers(root: HTMLElement): () => void {
    const saved: { el: HTMLElement; maxHeight: string; overflow: string; overflowY: string }[] = [];

    root.querySelectorAll<HTMLElement>('*').forEach((el) => {
        const cs = getComputedStyle(el);
        if (
            cs.overflow === 'auto' ||
            cs.overflow === 'scroll' ||
            cs.overflowY === 'auto' ||
            cs.overflowY === 'scroll'
        ) {
            saved.push({
                el,
                maxHeight: el.style.maxHeight,
                overflow: el.style.overflow,
                overflowY: el.style.overflowY,
            });
            el.style.maxHeight = 'none';
            el.style.overflow = 'visible';
            el.style.overflowY = 'visible';
        }
    });

    return () =>
        saved.forEach(({ el, maxHeight, overflow, overflowY }) => {
            el.style.maxHeight = maxHeight;
            el.style.overflow = overflow;
            el.style.overflowY = overflowY;
        });
}

/**
 * Capture any DOM element (charts, tables, metric panels) as a PNG blob.
 * Uses html-to-image which handles both SVG-based charts and HTML tables.
 * Elements with class "export-exclude" are hidden in the output.
 * Scroll containers are temporarily expanded so the full content is captured.
 */
export async function elementToPngBlob(element: HTMLElement, scale = 2): Promise<Blob> {
    const restore = expandScrollContainers(element);
    try {
        const dataUrl = await toPng(element, {
            pixelRatio: scale,
            backgroundColor: colors.paper,
            skipFonts: true,
            filter: exportFilter,
        });

        const [header, base64] = dataUrl.split(',');
        const mime = header.match(/:(.*?);/)?.[1] ?? 'image/png';
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return new Blob([bytes], { type: mime });
    } finally {
        restore();
    }
}
